<template lang="pug">
.group-details
    .gd-header
        .gd-title-row
            .gd-title {{ group.label || group.group_key }}
            font-awesome-icon.gd-settings(
                :icon="['fas', 'ellipsis-vertical']"
                :title="$t(`${baseTranslationPath}.group_settings_title`)"
                @click="$emit('open-settings')"
            )
        .gd-meta
            span.tag(v-if="group.group_type") {{ group.group_type }}
            span.tag(v-if="group.semantic_class") {{ group.semantic_class }}
            span.tag(v-if="group.interaction_class") {{ group.interaction_class }}
            span.tag.manual(v-if="group.source === 'manual'") {{ $t(`${baseTranslationPath}.manual`) }}

    //- Member fields (form_fields.group_ref === group.id).
    .gd-section
        .gd-section-title {{ $t(`${baseTranslationPath}.group_fields_heading`, { count: fields.length }) }}
        .gd-empty(v-if="!fields.length") {{ $t(`${baseTranslationPath}.group_no_fields`) }}
        .gd-item(
            v-for="(field, index) in fields"
            :key="field.id"
            :class="{ active: field.id === selectedFieldId }"
            @click="$emit('select-field', field)"
        )
            .gd-index {{ index + 1 }}
            font-awesome-icon.gd-icon(:icon="getFieldIconType(field).icon")
            .gd-body
                .gd-label {{ field.field_name || field.label || field.field_key }}
                .gd-sub
                    span.tag {{ field.field_type }}
                    span.tag.unlinked(v-if="!field.question_ref") {{ $t(`${baseTranslationPath}.unlinked`) }}
            font-awesome-icon.gd-settings(
                :icon="['fas', 'ellipsis-vertical']"
                :title="$t(`${baseTranslationPath}.field_settings_title`)"
                @click.stop="$emit('open-field-settings', field)"
            )

    //- Questions whose fields fall in this group (derived from shared fields).
    .gd-section
        .gd-section-title {{ $t(`${baseTranslationPath}.group_questions_heading`, { count: questions.length }) }}
        .gd-empty(v-if="!questions.length") {{ $t(`${baseTranslationPath}.group_no_questions`) }}
        .gd-item(
            v-for="question in questions"
            :key="question.id"
            @click="$emit('select-question', question)"
        )
            font-awesome-icon.gd-icon(:icon="['fas', 'circle-question']")
            .gd-body
                .gd-label {{ question.prompt || question.question_key }}
                .gd-sub
                    span.tag(v-if="question.answer_type") {{ question.answer_type }}
                    span.tag(v-if="question.interaction_class") {{ question.interaction_class }}
</template>
<script>
import get from 'lodash/get';
import { fieldIconTypes } from './fieldUtil';

export default {
  name: 'GroupDetails',
  props: {
    // selected group row (form_groups), enriched with field_ids/question_keys.
    group: {
      type: Object,
      required: true,
    },
    // form_fields rows whose group_ref points at this group.
    fields: {
      type: Array,
      default: () => [],
    },
    // questions whose fields fall in this group (derived).
    questions: {
      type: Array,
      default: () => [],
    },
    selectedFieldId: {
      type: [String, Number],
      default: null,
    },
  },
  emits: ['select-field', 'select-question', 'open-field-settings', 'open-settings'],
  data() {
    return {
      baseTranslationPath: 'iq_tools_tab.forms_tab.manage',
    };
  },
  methods: {
    getFieldIconType(field) {
      return fieldIconTypes(get(field, 'field_type'));
    },
  },
};
</script>
<style lang="scss" scoped>
@import 'src/styles/aiq-variables.scss';

.group-details {
  height: 100%;
  overflow-y: auto;
  padding: 14px;
}

.gd-header {
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid $aiq-tab-content-border;
}

.gd-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.gd-title {
  font-size: 16px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gd-meta {
  display: flex;
  gap: 6px;
  margin-top: 6px;
  flex-wrap: wrap;
}

.gd-section {
  margin-bottom: 18px;
}

.gd-section-title {
  font-size: 13px;
  font-weight: 700;
  color: $aiq-color-text-secondary;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.gd-empty {
  color: $aiq-color-text-secondary;
  font-size: 13px;
  padding: 10px;
  text-align: center;
}

.gd-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  border: 1px solid $aiq-icon-primary;
  border-radius: 5px;
  margin-bottom: 8px;
  cursor: pointer;
  background-color: $aiq-bgc-list-item;

  &:hover {
    background-color: $aiq-tab-content-background;
    border-color: $aiq-tab-content-background;
  }

  &.active {
    background-color: $aiq-tab-content-background;
    border-color: $aiq-bgc-button-primary;
  }
}

.gd-index {
  width: 22px;
  height: 22px;
  border-radius: 4px;
  background-color: $aiq-color-black;
  color: $aiq-color-white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

.gd-icon {
  color: $aiq-input-border-hover;
  font-size: 16px;
}

.gd-body {
  min-width: 0;
  flex: 1;
}

.gd-label {
  font-size: 14px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gd-sub {
  display: flex;
  gap: 6px;
  margin-top: 4px;
  flex-wrap: wrap;
}

.gd-settings {
  color: $aiq-input-border-hover;
  font-size: 15px;
  padding: 4px 6px;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    color: $aiq-bgc-button-primary;
  }
}

.tag {
  font-size: 11px;
  color: $aiq-input-border-hover;
  border: 1px solid $aiq-icon-primary;
  border-radius: 3px;
  padding: 0 5px;

  &.manual {
    color: $aiq-bgc-button-primary;
    border-color: $aiq-bgc-button-primary;
  }

  &.unlinked {
    color: $aiq-color-error;
    border-color: $aiq-color-error;
  }
}
</style>
